/**
 * TransactionRow - Component for displaying a single transaction in a list
 */
import { Check, TrendingUp, PiggyBank, Wallet, Tag, RefreshCw, Banknote } from "lucide-react";
import { useEffect, useRef } from "react";
import { getTransactionEmoji, getCategoryDisplayName } from "../../utils/categoryEmoji";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_08

const TransactionRow = ({ 
  transaction, 
  showDate, 
  budgetSettings = {}, 
  editingTransaction, 
  editTransactionData, 
  setEditTransactionData,
  editCategorySearch,
  setEditCategorySearch,
  showEditCategoryDropdown,
  setShowEditCategoryDropdown,
  editFilteredCategories = [],
  selectEditCategory,
  saveEditTransaction,
  cancelEditTransaction,
  startEditTransaction,
  removeTransaction,
  toggleTransactionCompleted,
  setViewingTransaction,
  getCardColors,
  getAccountName,
  getDebtName,
  formatCurrency,
  colorMap,
  cards = [],
  debts = [],
  theme = 'pink'
}) => {
  const isEditing = editingTransaction === transaction.id;
  const rowRef = useRef(null);

  useEffect(() => {
    if (!isEditing) return;
    // Scroll row into view when edit opens
    const timer = setTimeout(() => {
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    // When keyboard appears, scroll again to keep row visible
    const onKeyboardShow = () => {
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    };
    window.addEventListener('keyboardWillShow', onKeyboardShow);
    window.addEventListener('keyboardDidShow', onKeyboardShow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keyboardWillShow', onKeyboardShow);
      window.removeEventListener('keyboardDidShow', onKeyboardShow);
    };
  }, [isEditing]);
  const isWarm = theme === 'warm';

  // Warm theme color palette
  const warmColors = {
    income: { bg: '#E4EDE6', text: '#2D5A3D', icon: '#2D5A3D' },
    savings: { bg: '#E8EDE4', text: '#5D6B4D', icon: '#5D6B4D' },
    withdrawal: { bg: '#F0E8E0', text: '#8B6B4D', icon: '#8B6B4D' },
    debt: { bg: '#F0E8E0', text: '#8B6B4D', icon: '#8B6B4D' },
    recurring: { bg: '#E4EDED', text: '#4A6B6B' }
  };

  // Hex color map for inline styles
  const hexColorMap = {
    grey: { bg: '#9FA2AC', bgLight: '#E4E3E9', text: '#6B6B78' },
    red: { bg: '#E6827D', bgLight: '#F6C8C4', text: '#C4443F' },
    blue: { bg: '#7C9FE6', bgLight: '#C9D8F5', text: '#3F63C4' },
    orange: { bg: '#F0A06C', bgLight: '#FAD4B8', text: '#B8683F' },
    green: { bg: '#6EC08F', bgLight: '#C8E8D3', text: '#3F8A5F' },
    pink: { bg: '#E27FAB', bgLight: '#F7CCDE', text: '#C23A72' },
    purple: { bg: '#A488E0', bgLight: '#DDD1F2', text: '#7A5FB0' }
  };

  // Get the card color for this transaction (if it's an expense with a card)
  const getTransactionCardColor = () => {
    if (transaction.is_income || transaction.is_savings) return null;
    // Try card_id first, then payment_method
    if (transaction.card_id) {
      const card = cards?.find(c => c.id === transaction.card_id);
      return card?.color || null;
    }
    if (transaction.payment_method) {
      const card = cards?.find(c => c.name === transaction.payment_method);
      return card?.color || null;
    }
    return null;
  };

  const cardColor = getTransactionCardColor();
  const cardHexColors = cardColor ? hexColorMap[cardColor] : null;

  const txEmoji = getTransactionEmoji(transaction, budgetSettings);

  const themeBubbleBg = {
    pink: {
      income: '#dcfce7', savings: '#f3e8ff', withdrawal: '#fef3c7',
      debt: '#fee2e2', transfer: '#fce7f3', investment: '#dbeafe',
      emoji: '#fce7f3', fallback: '#f3f4f6'
    },

    blue: {
      income: '#dcfce7', savings: '#dbeafe', withdrawal: '#fef3c7',
      debt: '#fee2e2', transfer: '#e0f2fe', investment: '#dbeafe',
      emoji: '#e0f2fe', fallback: '#f3f4f6'
    },

    warm: {
      income: '#E4EDE6', savings: '#E8EDE4', withdrawal: '#F0E8E0',
      debt: '#F0E8E0', transfer: '#EDE8E0', investment: '#E8EDE4',
      emoji: '#EDE8E0', fallback: '#f3f4f6'
    }
  };
  // Purple (admin-only): every bubble is the same soft lavender.
  const purple = usePurple();
  const plan = usePlan();
  const bubblePalette = plan
    ? { income: '#F5F2FA', savings: '#F5F2FA', withdrawal: '#F5F2FA', debt: '#F5F2FA', transfer: '#F5F2FA', investment: '#F5F2FA', emoji: '#F5F2FA', fallback: '#F5F2FA' }
    : purple
    ? { income: '#F6F4F8', savings: '#F6F4F8', withdrawal: '#F6F4F8', debt: '#F6F4F8', transfer: '#F6F4F8', investment: '#F6F4F8', emoji: '#F6F4F8', fallback: '#F6F4F8' }
    : themeBubbleBg[theme] || themeBubbleBg.pink;

  const getBubbleBg = () => {
    if (transaction.completed) return '#f3f4f6';
    if (transaction.is_income) return bubblePalette.income;
    if (transaction.is_interest) return bubblePalette.investment;
    if (transaction.is_transfer) return bubblePalette.transfer;
    if (transaction.is_debt_payment) return bubblePalette.debt;
    if (transaction.is_savings) return transaction.transaction_type === 'withdrawal' ? bubblePalette.withdrawal : bubblePalette.savings;
    if (txEmoji) return bubblePalette.emoji;
    if (purple || plan) return bubblePalette.fallback;
    return cardHexColors?.bgLight || bubblePalette.fallback;
  };

  return (
    <div 
      ref={rowRef}
      className={"px-4 py-2.5 hover:theme-bg-50/50 transition-colors group cursor-pointer" + (plan ? " wy-plan-txrow" : "")}
      onClick={() => !isEditing && setViewingTransaction(transaction)}
      onTouchMove={() => { if (isEditing) document.activeElement?.blur(); }}
    >
      {isEditing ? (
        // Edit mode - clean stacked accordion layout
        <div className="py-3 space-y-3" onClick={(e) => e.stopPropagation()}>
          {/* Row 1: Amount + Date side by side */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">Amount</label>
              <Input 
                type="number" 
                value={editTransactionData.amount} 
                onChange={(e) => setEditTransactionData(prev => ({ ...prev, amount: e.target.value }))} 
                placeholder="0.00" 
                className="h-9 text-sm theme-border-200" 
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">Date</label>
              <Input 
                type="date" 
                value={editTransactionData.date || ''} 
                onChange={(e) => setEditTransactionData(prev => ({ ...prev, date: e.target.value }))} 
                className="h-9 text-sm theme-border-200 [&::-webkit-calendar-picker-indicator]:opacity-50" 
              />
            </div>
          </div>

          {/* Row 2: Category (expenses only) */}
          {!transaction.is_income && !transaction.is_savings && (
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">Category</label>
              <div className="relative">
                <Input
                  type="text"
                  value={editCategorySearch || editTransactionData.category}
                  onChange={(e) => {
                    setEditCategorySearch(e.target.value);
                    setEditTransactionData(prev => ({ ...prev, category: e.target.value }));
                    setShowEditCategoryDropdown(true);
                  }}
                  onFocus={() => setShowEditCategoryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowEditCategoryDropdown(false), 150)}
                  placeholder="Category"
                  className="h-9 text-sm theme-border-200"
                />
                {showEditCategoryDropdown && editFilteredCategories.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border theme-border-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {editFilteredCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => selectEditCategory(cat)}
                        className="w-full px-3 py-2 text-left text-sm hover:theme-bg-50"
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Row 3: Description */}
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">Note</label>
            <Input 
              type="text" 
              value={editTransactionData.description} 
              onChange={(e) => setEditTransactionData(prev => ({ ...prev, description: e.target.value }))} 
              placeholder="Optional" 
              className="h-9 text-sm theme-border-200" 
            />
          </div>

          {/* Row 4: Card/Payment method (expenses only) */}
          {!transaction.is_income && !transaction.is_savings && !transaction.is_transfer && (
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">Card</label>
              <Select 
                value={editTransactionData.paymentMethod || 'none'} 
                onValueChange={(value) => {
                  const cardValue = value === 'none' ? '' : value;
                  setEditTransactionData(prev => ({ ...prev, paymentMethod: cardValue }));
                }}
              >
                <SelectTrigger className="w-full h-9 text-sm theme-border-200">
                  <SelectValue placeholder="Select card" />
                </SelectTrigger>
                <SelectContent>
                  {(cards || []).map(card => (
                    <SelectItem key={card.name} value={card.name}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                        {card.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Card selection for savings transactions */}
          {transaction.is_savings && (
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">
                {transaction.transaction_type === 'deposit' ? 'From Card' : 'To Card'}
              </label>
              <Select 
                value={transaction.transaction_type === 'deposit' ? (editTransactionData.from_card || 'none') : (editTransactionData.to_card || 'none')} 
                onValueChange={(value) => {
                  const cardValue = value === 'none' ? '' : value;
                  if (transaction.transaction_type === 'deposit') {
                    setEditTransactionData(prev => ({ ...prev, from_card: cardValue }));
                  } else {
                    setEditTransactionData(prev => ({ ...prev, to_card: cardValue }));
                  }
                }}
              >
                <SelectTrigger className="w-full h-9 text-sm theme-border-200">
                  <SelectValue placeholder={transaction.transaction_type === 'deposit' ? "From card" : "To card"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(cards || []).filter(c => c.type !== 'credit').map(card => (
                    <SelectItem key={card.name} value={card.name}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                        {card.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Card selection for income transactions */}
          {transaction.is_income && !transaction.is_interest && (
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1 block">To Card</label>
              <Select 
                value={editTransactionData.to_card || 'none'} 
                onValueChange={(value) => {
                  const cardValue = value === 'none' ? '' : value;
                  setEditTransactionData(prev => ({ ...prev, to_card: cardValue }));
                }}
              >
                <SelectTrigger className="w-full h-9 text-sm theme-border-200">
                  <SelectValue placeholder="To card" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(cards || []).filter(c => c.type !== 'credit').map(card => (
                    <SelectItem key={card.name} value={card.name}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                        {card.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Save / Cancel buttons */}
          <div className="flex gap-2 pt-1">
            <Button onClick={(e) => { e.stopPropagation(); saveEditTransaction(); }} size="sm" className="flex-1 h-9 theme-bg-500 hover:theme-bg-600 text-white">
              <Check className="w-3.5 h-3.5 mr-1.5" /> Save
            </Button>
            <Button onClick={(e) => { e.stopPropagation(); cancelEditTransaction(); }} variant="outline" size="sm" className="flex-1 h-9 theme-border-200">
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        // View mode - compact
        <div className={`flex items-center gap-2 ${budgetSettings.show_checkmarks && transaction.completed ? 'opacity-50' : ''}`}>
          {/* Checkbox */}
          {budgetSettings.show_checkmarks && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleTransactionCompleted(transaction.id); }}
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                transaction.completed 
                  ? 'bg-gray-400 border-gray-400' 
                  : transaction.is_income 
                    ? (transaction.is_interest ? 'border-blue-300' : 'border-emerald-300')
                    : transaction.is_savings
                      ? (transaction.transaction_type === 'withdrawal' ? 'border-amber-300' : 'border-purple-300')
                      : 'theme-border-300'
              }`}
            >
              {transaction.completed && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </button>
          )}
          
          {/* Icon - emoji if available, else lucide icon fallback */}
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: getBubbleBg() }}
          >
            {txEmoji ? (
              <span className="text-base leading-none">{txEmoji}</span>
            ) : transaction.is_income ? (
              <TrendingUp
                className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : ''}`}
                style={!transaction.completed ? { color: isWarm ? warmColors.income.icon : '#10b981' } : undefined}
              />
            ) : transaction.is_savings ? (
              transaction.transaction_type === 'withdrawal'
                ? <Wallet className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : ''}`} style={!transaction.completed ? { color: isWarm ? warmColors.withdrawal.icon : '#f59e0b' } : undefined} />
                : <PiggyBank className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : ''}`} style={!transaction.completed ? { color: isWarm ? warmColors.savings.icon : '#8b5cf6' } : undefined} />
            ) : transaction.is_debt_payment ? (
              <Wallet className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : ''}`} style={!transaction.completed ? { color: isWarm ? warmColors.debt.icon : '#d97706' } : undefined} />
            ) : (
              <span className="text-base leading-none">🏷️</span>
            )}
          </div>
          {/* Details */}
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-normal truncate ${
              transaction.completed 
                ? 'text-gray-400 line-through' 
                : 'text-gray-800'
            }`}>
              {transaction.is_income 
                ? (transaction.description || (transaction.is_interest ? 'Interest' : 'Income')) 
                : transaction.is_savings 
                  ? (transaction.transaction_type === 'withdrawal' ? 'Withdrawal' : (transaction.description || 'Deposit')) 
                  : transaction.is_debt_payment
                    ? `Payment to ${getDebtName ? getDebtName(transaction.debt_id) : (transaction.description || 'Debt')}`
                    : getCategoryDisplayName(transaction.category)}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {!transaction.is_income && !transaction.is_savings && transaction.payment_method && (
                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{transaction.payment_method}</span>
              )}
              {transaction.is_income && transaction.to_card && (() => {
                const toCard = cards?.find(c => c.name === transaction.to_card);
                const toHex = toCard?.color ? hexColorMap[toCard.color] : null;
                return (
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">→ {transaction.to_card}</span>
                );
              })()}
              {transaction.is_savings && transaction.transaction_type === 'deposit' && transaction.from_card && (() => {
                const fromCard = cards?.find(c => c.name === transaction.from_card);
                const fromHex = fromCard?.color ? hexColorMap[fromCard.color] : null;
                return (
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">from {transaction.from_card}</span>
                );
              })()}
              {transaction.is_savings && transaction.transaction_type === 'withdrawal' && transaction.to_card && (() => {
                const toCard = cards?.find(c => c.name === transaction.to_card);
                const toHex = toCard?.color ? hexColorMap[toCard.color] : null;
                return (
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">to {transaction.to_card}</span>
                );
              })()}
              {transaction.is_recurring && (
                <span className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: isWarm ? warmColors.recurring.bg : '#dbeafe', color: isWarm ? warmColors.recurring.text : '#2563eb' }}>
                  <RefreshCw className="w-2.5 h-2.5" />
                </span>
              )}
              {transaction.description && !transaction.is_income && !transaction.is_savings && (
                <span className="text-xs text-gray-400 truncate">{transaction.description}</span>
              )}
              {(transaction.is_savings || transaction.is_interest) && (transaction.account_id || transaction.interest_account_id) && (
                <span className="text-xs text-gray-400 truncate">
                  → {getAccountName(transaction.is_interest ? transaction.interest_account_id : transaction.account_id, transaction.is_interest ? transaction.interest_account_type : transaction.account_type)}
                </span>
              )}
              {showDate && (
                <span className="text-xs text-gray-400">{new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              )}
            </div>
          </div>
          
          {/* Amount */}
          <span className={`text-sm font-bold text-right min-w-[70px] flex-shrink-0 ${
            transaction.completed 
              ? 'text-gray-400 line-through' 
              : transaction.is_income 
                ? ''
                : 'text-gray-700'
          }`} style={!transaction.completed && transaction.is_income ? { color: plan ? '#2F8756' : isWarm ? warmColors.income.text : '#10b981' } : undefined}>
            {transaction.is_income ? '+' : '-'}{formatCurrency(transaction.amount)}
          </span>
        </div>
      )}
    </div>
  );
};

export default TransactionRow;
